import React, { useMemo, useState } from "react";
import moment from "moment";
import { Badge, Button, Collapse, Row, Col } from "reactstrap";
import { BlockTitle } from "../../Component";
import PartnerTeleconsultationClinicsChannelsTable from "./PartnerTeleconsultationClinicsChannelsTable";
import {
  normalizeTcApprovalStatus,
  tcApprovalSelectValueFromPartner,
  tcApprovalApiStatusFromSelectValue,
  tcApprovalStatusBadgeLabel,
  PARTNER_TC_PLATFORM_SELECT_OPTIONS,
} from "../medical-partner-admin/medicalPartnerAdminConstants";

/** Partner dossier — Teleconsultation tab: partner approval + per-clinic channel table. */
const PartnerTeleconsultationTab = ({
  partner,
  clinicsSummary,
  publicWebBase,
  tcSaving,
  onSavePartnerTc,
  clinicChannelBusyId,
  onClinicTeleconsultationPatch,
  onRequestAddClinic,
}) => {
  const [showAdvancedIds, setShowAdvancedIds] = useState(false);

  const channelStats = useMemo(() => {
    const list = Array.isArray(clinicsSummary) ? clinicsSummary : [];
    return {
      total: list.length,
      offersTc: list.filter((c) => !!c.offersTeleconsultation).length,
      platformOk: list.filter((c) => String(c.platformListingDecision || "").toLowerCase() === "approved").length,
    };
  }, [clinicsSummary]);

  if (!partner) return null;

  const tcSelectValue = tcApprovalSelectValueFromPartner(partner);
  const tcBadgeLabel = tcApprovalStatusBadgeLabel(partner);

  return (
    <div>
      <Row className="g-gs mb-3">
        <Col md="12">
          <div className="card card-bordered">
            <div className="card-inner">
              <BlockTitle tag="h5" className="mb-2">
                Partner teleconsultation
              </BlockTitle>
              <p className="text-soft mb-2">
                Approve teleconsultation at partner level, then turn on per-clinic channels below. Catalog listing for clinics and practitioners
                is decided in Practitioner approvals — not via the retired Platform TC switch.
              </p>
              {channelStats.total > 0 ? (
                <p className="small text-soft mb-3">
                  <strong className="text-dark">{channelStats.total}</strong> clinic{channelStats.total !== 1 ? "s" : ""} ·{" "}
                  <strong className="text-dark">{channelStats.offersTc}</strong> offer TC ·{" "}
                  <strong className="text-dark">{channelStats.platformOk}</strong> catalog approved
                </p>
              ) : (
                <p className="small text-soft mb-3">No clinics on file yet — add one to configure channels.</p>
              )}
              <div className="d-flex flex-wrap align-items-center mb-2">
                <span className="text-soft small me-2">Platform TC:</span>
                <Badge
                  className="badge-dim"
                  color={tcBadgeLabel === "On" ? "success" : tcBadgeLabel === "Pending" ? "warning" : "light"}
                >
                  {tcBadgeLabel}
                </Badge>
                {normalizeTcApprovalStatus(partner) === "rejected" ? (
                  <span className="text-soft small ms-2">(API: rejected — choose Off to clear to inactive)</span>
                ) : null}
                <span className="text-soft small ms-3">
                  Last update:{" "}
                  {partner.tcApprovalUpdatedAt ? moment(partner.tcApprovalUpdatedAt).format("DD/MM/YYYY HH:mm") : "—"}
                </span>
              </div>
              <p className="small text-soft mb-3">Admin note: {partner.tcApprovalNote || "—"}</p>
              <div className="d-flex flex-wrap align-items-end mb-3">
                <div style={{ minWidth: 220 }}>
                  <label className="form-label small text-soft mb-1" htmlFor="partner-tc-platform-select">
                    Partner teleconsultation (platform)
                  </label>
                  <select
                    id="partner-tc-platform-select"
                    className="form-control form-control-sm"
                    value={tcSelectValue}
                    disabled={tcSaving}
                    onChange={(e) => {
                      const nextApi = tcApprovalApiStatusFromSelectValue(e.target.value);
                      const currentApi = normalizeTcApprovalStatus(partner);
                      if (nextApi === currentApi) return;
                      void onSavePartnerTc(nextApi, partner.tcApprovalNote || "");
                    }}
                  >
                    {PARTNER_TC_PLATFORM_SELECT_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                {tcSaving ? (
                  <span className="text-soft small ml-2 mb-1">Saving…</span>
                ) : null}
              </div>
              <Button color="link" size="sm" className="p-0 mb-2 d-block" type="button" onClick={() => setShowAdvancedIds((v) => !v)}>
                {showAdvancedIds ? "Hide" : "Show"} technical details
              </Button>
              <Collapse isOpen={showAdvancedIds}>
                <p className="small text-muted mb-1">
                  Partner document ID: <code>{partner._id}</code>
                </p>
                {!publicWebBase ? (
                  <p className="small text-muted mb-0">
                    Patient site base URL is not configured in this admin build environment — operators still manage slugs; full links preview
                    in environments where the variable is set.
                  </p>
                ) : null}
              </Collapse>
            </div>
          </div>
        </Col>
      </Row>

      {Array.isArray(clinicsSummary) && clinicsSummary.length > 0 ? (
        <Row className="g-gs mb-3">
          <Col md="12">
            <PartnerTeleconsultationClinicsChannelsTable
              clinicsSummary={clinicsSummary}
              busyClinicId={clinicChannelBusyId}
              onClinicChannelPatch={onClinicTeleconsultationPatch}
            />
          </Col>
        </Row>
      ) : (
        <div className="alert alert-light border mb-0" role="status">
          <p className="mb-2 small text-soft">No clinic summary loaded for this partner.</p>
          <Button color="primary" size="sm" type="button" onClick={onRequestAddClinic}>
            Open clinics
          </Button>
        </div>
      )}
    </div>
  );
};

export default PartnerTeleconsultationTab;
